import { BAZAAR_SHARED_LEDGER, fetchBazaarCollections, fetchBazaarLedgerCanisters, } from "./bazaarRegistry.js";
import { fetchDgdgPortfolioByPrincipal } from "./portfolio.js";
import { fetchEntrepotCapPortfolioByPrincipal } from "./entrepot.js";
import { fetchIcrc7Nfts } from "./icrc7Portfolio.js";
import { fetchExtPortfolioForCanister, fetchExtPortfolioScan } from "./extPortfolio.js";
import { getCoverageByTier, getOrderedExtScanCanisters } from "./holdingsCoverage.js";
import { canonicalizeDirectMedia } from "./directMedia.js";
import { canonicalIcpTokenId, icpPortfolioNftKey } from "./tokenId.js";
import { normalizeIcpCanisterId } from "./icAgent.js";
import { resolveCollectionDisplayName } from "./collectionNames.js";
function mapPortfolio(n, standard) {
    const contractId = normalizeIcpCanisterId(n.contractId) ?? n.contractId.trim().toLowerCase();
    const tokenId = canonicalIcpTokenId(contractId, n.tokenId);
    const media = canonicalizeDirectMedia({
        preferred: n.imageUrl,
        animationUrl: n.animationUrl,
        canisterId: contractId,
        tokenId,
    });
    const collectionName = resolveCollectionDisplayName(contractId, n.collectionName);
    const bazaarCollectionId = n.bazaarCollectionId?.trim() || null;
    // Prefer ledger#collectionId for multi-series Bazaar ledgers so groups stay stable.
    const groupKey = n.groupKey?.trim() ||
        (bazaarCollectionId ? `${contractId}#${bazaarCollectionId}` : contractId);
    return {
        contractId,
        tokenId,
        name: n.name,
        imageUrl: media.posterUrl ?? media.mediaUrl,
        mediaUrl: media.mediaUrl,
        mediaKind: media.mediaKind,
        posterUrl: media.posterUrl,
        collectionName,
        groupKey,
        standard,
        bazaarCollectionId,
        catalogSerial: n.catalogSerial ?? null,
        attributes: n.attributes,
        description: n.description ?? null,
        utilityNotes: n.utilityNotes ?? null,
    };
}
function mergeInto(bag, items) {
    for (const n of items) {
        const key = icpPortfolioNftKey(n.contractId, n.tokenId);
        const prev = bag.get(key);
        if (!prev) {
            bag.set(key, n);
            continue;
        }
        // Prefer richer Bazaar metadata / ICRC-7 over thin index stubs.
        const score = (x) => (x.bazaarCollectionId ? 4 : 0) +
            (x.catalogSerial ? 2 : 0) +
            (x.standard === "icrc7" ? 2 : x.standard === "ext" ? 1 : 0) +
            (x.imageUrl ? 1 : 0);
        if (score(n) > score(prev))
            bag.set(key, n);
    }
    return [...bag.values()];
}
function holdingRichness(x) {
    return ((x.bazaarCollectionId ? 4 : 0) +
        (x.catalogSerial ? 2 : 0) +
        (x.standard === "icrc7" ? 2 : x.standard === "ext" ? 1 : 0) +
        (x.imageUrl ? 1 : 0));
}
/**
 * Drop duplicate Bazaar rows: same series+catalog serial under different token ids
 * (index stubs used mint/serial as tokenId; ICRC-7 uses global ledger id).
 */
function collapseDuplicateHoldings(items) {
    const byToken = new Map();
    for (const n of items) {
        const key = icpPortfolioNftKey(n.contractId, n.tokenId);
        const prev = byToken.get(key);
        if (!prev || holdingRichness(n) > holdingRichness(prev)) {
            byToken.set(key, n);
        }
    }
    const bySeriesSerial = new Map();
    for (const [tokenKey, n] of byToken) {
        const serial = n.catalogSerial?.trim();
        const cid = n.bazaarCollectionId?.trim();
        if (!serial || !cid || !/^\d+$/.test(serial) || !/^\d+$/.test(cid))
            continue;
        const sk = `${n.contractId}#${cid}#${serial}`;
        const existing = bySeriesSerial.get(sk);
        if (!existing) {
            bySeriesSerial.set(sk, tokenKey);
            continue;
        }
        if (existing === tokenKey)
            continue;
        const a = byToken.get(existing);
        const b = n;
        if (!a) {
            bySeriesSerial.set(sk, tokenKey);
            continue;
        }
        if (holdingRichness(b) > holdingRichness(a)) {
            byToken.delete(existing);
            bySeriesSerial.set(sk, tokenKey);
        }
        else {
            byToken.delete(tokenKey);
        }
    }
    // Index stubs on the shared ledger that used catalog serial as tokenId.
    const knownSerials = new Set();
    for (const n of byToken.values()) {
        if (n.contractId === BAZAAR_SHARED_LEDGER &&
            n.catalogSerial &&
            /^\d+$/.test(n.catalogSerial)) {
            knownSerials.add(n.catalogSerial);
        }
    }
    if (knownSerials.size) {
        for (const [tokenKey, n] of [...byToken.entries()]) {
            if (n.standard !== "index")
                continue;
            if (n.contractId !== BAZAAR_SHARED_LEDGER)
                continue;
            if (n.catalogSerial)
                continue;
            if (knownSerials.has(n.tokenId))
                byToken.delete(tokenKey);
        }
    }
    return [...byToken.values()];
}
function throwIfAborted(signal) {
    if (signal?.aborted) {
        const err = new Error("Aborted");
        err.name = "AbortError";
        throw err;
    }
}
/**
 * Fast progressive holdings load for Canopy:
 * portfolio indexes + Bazaar ICRC-7 in parallel, then tiered concurrent EXT.
 */
export async function fetchCanopyHoldings(ownerPrincipal, options = {}) {
    if (!ownerPrincipal)
        return [];
    const { signal, warmCanisters = [], embed = false, onProgress, onBatch, } = options;
    const concurrency = options.concurrency ?? (embed ? 6 : 10);
    const maxExt = options.maxExtCanisters ??
        (embed ? 400 : Number.POSITIVE_INFINITY);
    const maxTier = options.maxTier ?? (embed ? 1 : 2);
    const bag = new Map();
    const emit = (phase) => {
        const all = collapseDuplicateHoldings([...bag.values()]);
        bag.clear();
        for (const n of all) {
            bag.set(icpPortfolioNftKey(n.contractId, n.tokenId), n);
        }
        onBatch?.(all, phase);
        return all;
    };
    onProgress?.("Loading NFT indexes…", "indexes");
    throwIfAborted(signal);
    const [dgdg, cap] = await Promise.all([
        fetchDgdgPortfolioByPrincipal(ownerPrincipal).catch(() => []),
        fetchEntrepotCapPortfolioByPrincipal(ownerPrincipal).catch(() => []),
    ]);
    throwIfAborted(signal);
    mergeInto(bag, dgdg.map((n) => mapPortfolio(n, "index")));
    mergeInto(bag, cap.map((n) => mapPortfolio(n, "index")));
    emit("indexes");
    onProgress?.("Scanning Bazaar collections…", "icrc7");
    /** Ledgers already filled via ICRC-7 — never re-probe as EXT (avoids duplicate rows). */
    const icrc7Resolved = new Set();
    try {
        /**
         * Live registry is authoritative — probe every non-Draft collection by
         * standard (ICRC-7 / EXT / fallback). Shared ledger once; unique ledgers
         * only. Attach bazaarCollectionId so Market links + sort work.
         */
        let bazaarRows = [];
        try {
            bazaarRows = (await fetchBazaarCollections())
                .filter((c) => c.status !== "Draft")
                .sort((a, b) => Number(a.id) - Number(b.id));
        }
        catch {
            bazaarRows = [];
        }
        const icrc7Ledgers = new Set();
        const extByLedger = new Map();
        for (const c of bazaarRows) {
            const ledger = normalizeIcpCanisterId(c.ledger) ?? c.ledger.trim().toLowerCase();
            if (!ledger)
                continue;
            if (c.standard === "EXT") {
                if (!extByLedger.has(ledger))
                    extByLedger.set(ledger, { ...c, ledger });
                continue;
            }
            // ICRC7, DIP721, Custom, Unknown — try ICRC-7 first
            icrc7Ledgers.add(ledger);
        }
        // Floor: shared + known external even if registry briefly empty
        try {
            for (const l of await fetchBazaarLedgerCanisters()) {
                const id = normalizeIcpCanisterId(l) ?? l.trim().toLowerCase();
                if (id)
                    icrc7Ledgers.add(id);
            }
        }
        catch {
            icrc7Ledgers.add(BAZAAR_SHARED_LEDGER);
        }
        // Shared ledger first (all Bazaar-native series in one probe)
        if (icrc7Ledgers.has(BAZAAR_SHARED_LEDGER)) {
            try {
                const shared = await fetchIcrc7Nfts(BAZAAR_SHARED_LEDGER, ownerPrincipal, {
                    maxTokens: 50_000,
                });
                mergeInto(bag, shared.map((t) => mapPortfolio({
                    chain: "icp",
                    contractId: BAZAAR_SHARED_LEDGER,
                    tokenId: t.tokenId.toString(),
                    name: t.name,
                    imageUrl: t.imageUrl,
                    animationUrl: t.animationUrl ?? null,
                    walletAddress: ownerPrincipal,
                    collectionName: t.collectionName,
                    groupKey: t.groupKey,
                    bazaarCollectionId: t.bazaarCollectionId,
                    catalogSerial: t.catalogSerial,
                    attributes: t.attributes,
                    description: t.description,
                    utilityNotes: t.utilityNotes,
                }, "icrc7")));
                icrc7Resolved.add(BAZAAR_SHARED_LEDGER);
            }
            catch {
                /* */
            }
        }
        const otherIcrc7 = [...icrc7Ledgers].filter((l) => l !== BAZAAR_SHARED_LEDGER);
        const bazaarConcurrency = Math.min(concurrency, 8);
        for (let i = 0; i < otherIcrc7.length; i += bazaarConcurrency) {
            throwIfAborted(signal);
            const chunk = otherIcrc7.slice(i, i + bazaarConcurrency);
            await Promise.all(chunk.map(async (ledger) => {
                try {
                    const tokens = await fetchIcrc7Nfts(ledger, ownerPrincipal, {
                        maxTokens: 50_000,
                    });
                    const meta = bazaarRows.find((c) => (normalizeIcpCanisterId(c.ledger) ?? c.ledger.trim().toLowerCase()) ===
                        ledger);
                    mergeInto(bag, tokens.map((t) => mapPortfolio({
                        chain: "icp",
                        contractId: ledger,
                        tokenId: t.tokenId.toString(),
                        name: t.name,
                        imageUrl: t.imageUrl,
                        animationUrl: t.animationUrl ?? null,
                        walletAddress: ownerPrincipal,
                        collectionName: t.collectionName ?? meta?.name ?? null,
                        groupKey: t.groupKey ?? (meta ? `${ledger}#${meta.id}` : ledger),
                        bazaarCollectionId: t.bazaarCollectionId ?? meta?.id ?? null,
                        catalogSerial: t.catalogSerial,
                        attributes: t.attributes,
                        description: t.description,
                        utilityNotes: t.utilityNotes,
                    }, "icrc7")));
                    if (tokens.length)
                        icrc7Resolved.add(ledger);
                }
                catch {
                    /* not ICRC-7 — EXT pass below / coverage scan may catch */
                }
            }));
        }
        // Bazaar-registered EXT collections (skipped if we only probed ICRC-7)
        const extLedgers = [...extByLedger.entries()].filter(([ledger]) => !icrc7Resolved.has(ledger));
        for (let i = 0; i < extLedgers.length; i += bazaarConcurrency) {
            throwIfAborted(signal);
            const chunk = extLedgers.slice(i, i + bazaarConcurrency);
            await Promise.all(chunk.map(async ([ledger, meta]) => {
                try {
                    const hits = await fetchExtPortfolioForCanister(ledger, ownerPrincipal, { maxTokens: 10_000 });
                    mergeInto(bag, hits.map((n) => mapPortfolio({
                        ...n,
                        collectionName: meta.name || n.collectionName,
                        groupKey: `${ledger}#${meta.id}`,
                        bazaarCollectionId: meta.id,
                    }, "ext")));
                }
                catch {
                    /* */
                }
            }));
        }
    }
    catch {
        /* registry down */
    }
    emit("icrc7");
    const ownedFromIndex = [...bag.values()]
        .map((n) => normalizeIcpCanisterId(n.contractId))
        .filter((id) => !!id);
    const warmPrefix = [...warmCanisters, ...ownedFromIndex];
    const ordered = getOrderedExtScanCanisters({
        warmCanisters: warmPrefix,
        maxTier,
    })
        .filter((id) => !icrc7Resolved.has(id))
        .slice(0, Number.isFinite(maxExt) ? maxExt : undefined);
    /**
     * Partition by real coverage membership — never use approximate hotN/warmN
     * offsets. Index/warm prefixes used to shove late tier-0 hits (Pokedbots,
     * Mutant Army, …) past the maxTier≤1 cutoff so popular EXT never scanned.
     */
    const warmPrefixSet = new Set(warmPrefix
        .map((c) => normalizeIcpCanisterId(c))
        .filter((id) => !!id));
    const t0Set = new Set(getCoverageByTier(0));
    const t1Set = new Set(getCoverageByTier(1));
    const hotIds = [];
    const warmIds = [];
    const fullIds = [];
    for (const id of ordered) {
        if (warmPrefixSet.has(id) || t0Set.has(id))
            hotIds.push(id);
        else if (t1Set.has(id))
            warmIds.push(id);
        else
            fullIds.push(id);
    }
    /** Probe EXT in chunks so holdings appear mid-scan (not only at phase end). */
    const EXT_CHUNK = embed ? 32 : 48;
    const runExt = async (slice, phase, labelBase) => {
        if (!slice.length)
            return;
        throwIfAborted(signal);
        onProgress?.(`${labelBase} (0/${slice.length})…`, phase);
        for (let offset = 0; offset < slice.length; offset += EXT_CHUNK) {
            throwIfAborted(signal);
            const chunk = slice.slice(offset, offset + EXT_CHUNK);
            const done = Math.min(offset + chunk.length, slice.length);
            onProgress?.(`${labelBase} (${done}/${slice.length})…`, phase);
            const hits = await fetchExtPortfolioScan(ownerPrincipal, chunk, {
                maxCanisters: chunk.length,
                maxTokensPerCanister: 10_000,
                concurrency,
                signal,
            });
            throwIfAborted(signal);
            mergeInto(bag, hits.map((n) => mapPortfolio(n, "ext")));
            emit(phase);
        }
    };
    if (maxTier >= 0) {
        await runExt(hotIds, "ext-tier0", "EXT hot scan");
    }
    if (maxTier >= 1) {
        await runExt(warmIds, "ext-tier1", "EXT warm scan");
    }
    if (maxTier >= 2) {
        await runExt(fullIds, "ext-tier2", "EXT full scan");
    }
    onProgress?.("Holdings ready", "done");
    return emit("done");
}
/** Canister IDs that produced at least one NFT — for session warm cache. */
export function extractHitCanisters(nfts) {
    const seen = new Set();
    const out = [];
    for (const n of nfts) {
        const id = normalizeIcpCanisterId(n.contractId);
        if (!id || seen.has(id))
            continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}
/**
 * Rescan a single collection canister for one owner — used by per-collection
 * Refresh buttons in Canopy / Bazaar profile.
 */
export async function rescanCanisterHoldings(ownerPrincipal, canisterId, options = {}) {
    const id = normalizeIcpCanisterId(canisterId);
    if (!id || !ownerPrincipal)
        return [];
    const prefer = options.standard ?? "auto";
    const bazaarId = options.bazaarCollectionId?.trim() || null;
    const tryIcrc7 = async () => {
        try {
            const tokens = await fetchIcrc7Nfts(id, ownerPrincipal, {
                maxTokens: 10_000,
            });
            let mapped = tokens.map((t) => mapPortfolio({
                chain: "icp",
                contractId: t.collectionId || id,
                tokenId: t.tokenId.toString(),
                name: t.name,
                imageUrl: t.imageUrl,
                animationUrl: t.animationUrl ?? null,
                walletAddress: ownerPrincipal,
                collectionName: t.collectionName ?? null,
                groupKey: t.groupKey,
                bazaarCollectionId: t.bazaarCollectionId,
                catalogSerial: t.catalogSerial,
                attributes: t.attributes,
                description: t.description,
                utilityNotes: t.utilityNotes,
            }, "icrc7"));
            if (bazaarId) {
                mapped = mapped.filter((n) => n.bazaarCollectionId === bazaarId);
            }
            return mapped;
        }
        catch {
            return [];
        }
    };
    const tryExt = async () => {
        try {
            const hits = await fetchExtPortfolioForCanister(id, ownerPrincipal, {
                maxTokens: 10_000,
            });
            return hits.map((n) => mapPortfolio(n, "ext"));
        }
        catch {
            return [];
        }
    };
    if (prefer === "icrc7")
        return tryIcrc7();
    if (prefer === "ext" || prefer === "index")
        return tryExt();
    if (bazaarId) {
        const icrc = await tryIcrc7();
        if (icrc.length)
            return icrc;
        return tryExt();
    }
    const ext = await tryExt();
    if (ext.length)
        return ext;
    return tryIcrc7();
}
